import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

/**
 * Create or edit a Promo Banner for a manufacturer.
 *
 * Fields:
 *  - title  (required)
 *  - description (optional, shown on the banner card)
 *  - flyer: either an external URL OR an uploaded PDF (mutually exclusive)
 *  - active toggle
 *  - expires_at (optional date)
 */
export default function PromoBannerFormDialog({
  open,
  onOpenChange,
  manufacturerId,
  banner, // null when creating
  onSaved,
}) {
  const isEdit = !!banner;
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [flyerUrl, setFlyerUrl] = useState("");
  const [flyerPdfId, setFlyerPdfId] = useState(null);
  const [flyerPdfName, setFlyerPdfName] = useState("");
  const [active, setActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (banner) {
      setTitle(banner.title || "");
      setDescription(banner.description || "");
      setFlyerUrl(banner.flyer_url || "");
      setFlyerPdfId(banner.flyer_pdf_id || null);
      setFlyerPdfName(banner.flyer_pdf_id ? "Attached PDF" : "");
      setActive(banner.active ?? true);
      setExpiresAt(banner.expires_at || "");
    } else {
      setTitle("");
      setDescription("");
      setFlyerUrl("");
      setFlyerPdfId(null);
      setFlyerPdfName("");
      setActive(true);
      setExpiresAt("");
    }
  }, [open, banner]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Flyer must be a PDF.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/pdfs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFlyerPdfId(data.id);
      setFlyerPdfName(data.filename || f.name);
      setFlyerUrl(""); // clear URL since we have a PDF now
      toast.success("Flyer PDF uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Could not upload PDF"));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        flyer_url: flyerPdfId ? null : flyerUrl.trim() || null,
        flyer_pdf_id: flyerPdfId || null,
        active,
        expires_at: expiresAt || null,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/promo-banners/${banner.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/promo-banners", {
          manufacturer_id: manufacturerId,
          ...payload,
        });
        saved = data;
      }
      toast.success(isEdit ? "Promo banner updated" : "Promo banner created");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Could not save banner"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="promo-banner-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit promo banner" : "Add promo banner"}</DialogTitle>
          <DialogDescription>
            Shown to users at the top of the search results after they pick this
            manufacturer. Auto-hides when expired or toggled off.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="promo-title" className="text-xs uppercase tracking-wider">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="promo-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Spring rebate on Area Lights"
              maxLength={120}
              required
              data-testid="promo-title-input"
              className="rounded-sm mt-1"
            />
          </div>

          <div>
            <Label htmlFor="promo-desc" className="text-xs uppercase tracking-wider">
              Description
            </Label>
            <Textarea
              id="promo-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short blurb shown on the banner card"
              rows={2}
              maxLength={240}
              data-testid="promo-description-input"
              className="rounded-sm mt-1 resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">
              Flyer link <span className="text-muted-foreground normal-case">(pick one)</span>
            </Label>
            <Input
              type="url"
              value={flyerUrl}
              onChange={(e) => {
                setFlyerUrl(e.target.value);
                if (e.target.value && flyerPdfId) {
                  setFlyerPdfId(null);
                  setFlyerPdfName("");
                }
              }}
              placeholder="https://example.com/promo.pdf"
              disabled={!!flyerPdfId}
              data-testid="promo-flyer-url-input"
              className="rounded-sm"
            />
            <div className="text-[11px] text-muted-foreground text-center">— or —</div>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={onPickFile}
                data-testid="promo-flyer-pdf-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm"
                disabled={uploading || !!flyerUrl}
                onClick={() => fileRef.current?.click()}
                data-testid="promo-upload-pdf-btn"
              >
                {uploading ? (
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-2" />
                )}
                {flyerPdfId ? "Replace PDF" : "Upload PDF"}
              </Button>
              {flyerPdfId && (
                <div className="flex-1 flex items-center justify-between gap-2 text-xs text-slate-600 bg-slate-50 border border-border rounded-sm px-2 py-1.5">
                  <span className="inline-flex items-center gap-1.5 truncate">
                    <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span className="truncate" data-testid="promo-flyer-pdf-name">
                      {flyerPdfName}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setFlyerPdfId(null);
                      setFlyerPdfName("");
                    }}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove flyer PDF"
                    data-testid="promo-remove-pdf-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="promo-expires" className="text-xs uppercase tracking-wider">
                Expires on
              </Label>
              <Input
                id="promo-expires"
                type="date"
                value={expiresAt ? expiresAt.slice(0, 10) : ""}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="promo-expires-input"
                className="rounded-sm mt-1"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Leave blank for no expiry.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider block">Status</Label>
              <div className="mt-2 flex items-center gap-2">
                <Switch
                  checked={active}
                  onCheckedChange={setActive}
                  data-testid="promo-active-toggle"
                />
                <span className="text-sm text-slate-700">
                  {active ? "Active" : "Hidden"}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              disabled={saving || uploading}
              data-testid="promo-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? "Save changes" : "Create banner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
